import type { Editor } from '@tiptap/core'
import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

export interface FrameEngagement {
  // Focus is within the node's own box.
  focused: boolean
  // One entry per open popover, so several (the instrument grid, History)
  // can be open together without one closing turning the others off.
  openPopovers: ReadonlySet<string>
  // Looks again, once a closing layer has taken its focus with it, whether
  // focus is still in the box.
  settleFocus: () => void
  reportPopoverOpen: (open: boolean, id?: string) => void
  // For the box's React focus events.
  onFocus: () => void
  onBlur: () => void
}

// Whether an observation node's box is being worked on: focus within it, or
// a popover opened from it (those portal out of the box, so they are
// tracked as open, never inferred from where focus is: a note inside a
// dialog has focus inside some dialog all the time).
export function useFrameEngagement(
  boxRef: RefObject<HTMLElement | null>
): FrameEngagement {
  const [focused, setFocused] = useState(false)
  const [openPopovers, setOpenPopovers] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const openPopoversRef = useRef<ReadonlySet<string>>(openPopovers)
  // When a popover or dialog from the node closes, the element that held
  // focus goes with it, and focus drops to the page with no blur. Look again
  // once it has settled.
  const blurTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  useEffect(() => () => clearTimeout(blurTimer.current), [])
  const settleFocus = useCallback((): void => {
    requestAnimationFrame(() => {
      if (!holdsFocus(boxRef.current, document.activeElement)) {
        setFocused(false)
      }
    })
  }, [boxRef])
  const reportPopoverOpen = useCallback(
    (open: boolean, id = 'popover'): void => {
      const current = openPopoversRef.current
      if (current.has(id) === open) {
        return
      }
      const next = new Set(current)
      if (open) {
        next.add(id)
      } else {
        next.delete(id)
      }
      openPopoversRef.current = next
      setOpenPopovers(next)
      if (next.size === 0) {
        settleFocus()
      }
    },
    [settleFocus]
  )
  // React focus events follow React's tree, so they reach the box from the
  // node's own popovers (portalled, but rendered from here) and from nothing
  // else. A move within that tree blurs and refocuses in one batch, and
  // stays true. The blur waits a tick for the focus that may follow it, so a
  // move inside the node never reads as leaving it.
  const onFocus = useCallback((): void => {
    clearTimeout(blurTimer.current)
    setFocused(true)
  }, [])
  const onBlur = useCallback((): void => {
    clearTimeout(blurTimer.current)
    blurTimer.current = setTimeout(() => setFocused(false))
  }, [])
  return {
    focused,
    openPopovers,
    settleFocus,
    reportPopoverOpen,
    onFocus,
    onBlur,
  }
}

const CONTROL =
  'input, textarea, select, button, a[href], [contenteditable="true"], [role="radio"], [role="combobox"], [role="option"], [role="button"], [data-observation-actions]'

// Whether a press at `target` is on one of the node's controls (or its
// actions, or a layer opened from it), which handle it themselves. The box,
// a button when at rest, is not one of them.
export function onControl(box: HTMLElement, target: EventTarget): boolean {
  if (!(target instanceof Element)) {
    return false
  }
  // In a popover or dialog opened from the node: its events reach the box
  // through React's tree, but they are that layer's own.
  if (!box.contains(target)) {
    return true
  }
  const control = target.closest(CONTROL)
  return !!control && control !== box && box.contains(control)
}

// The node's main control: one marked `data-observation-primary`, or else the
// first that takes focus (the value editor, the first answer, the picker's
// trigger). A text editor gets its caret at the end.
export function focusPrimary(box: HTMLElement): void {
  const candidates = box.querySelectorAll<HTMLElement & { editor?: Editor }>(
    '[data-observation-primary], [contenteditable="true"], button:not(:disabled), input:not(:disabled), [tabindex="0"]'
  )
  const primary =
    box.querySelector<HTMLElement & { editor?: Editor }>(
      '[data-observation-primary]'
    ) ??
    [...candidates].find(
      (candidate) => !candidate.closest('[data-observation-actions]')
    )
  // Focused at once, inside the click, then given its caret at the end once
  // TipTap's own focus has run on the next frame.
  primary?.focus()
  primary?.editor?.commands.focus('end')
}

// Whether focus at `target` is inside the node's own box.
function holdsFocus(
  box: HTMLElement | null,
  target: EventTarget | null
): boolean {
  return target instanceof Node && !!box?.contains(target)
}
